/**
 * Recently Played Hook
 * React Query hook for fetching user's listening history
 */

import { useQuery } from '@tanstack/react-query';
import { getApiClient } from '../client';
import type { RecentlyPlayedResponse } from '../types';

// Query key factory
export const recentlyPlayedKeys = {
  all: ['recently-played'] as const,
  lists: () => [...recentlyPlayedKeys.all, 'list'] as const,
  list: (options?: UseRecentlyPlayedOptions) => [...recentlyPlayedKeys.lists(), options] as const,
};

/**
 * Options for fetching recently played tracks
 */
export interface UseRecentlyPlayedOptions {
  /**
   * Number of items to return (1-50)
   * @default 50
   */
  limit?: number;
  /**
   * Unix timestamp in milliseconds
   * Returns all items before this cursor
   */
  before?: string;
  /**
   * Unix timestamp in milliseconds
   * Returns all items after this cursor
   */
  after?: string;
}

/**
 * Hook to fetch user's recently played tracks
 *
 * Uses cursor-based pagination via before/after timestamps.
 * Caches data for 1 minute (history doesn't change frequently).
 *
 * @param options - Query parameters for filtering and pagination
 * @returns Query result with recently played tracks
 *
 * @example
 * ```tsx
 * function HistoryView() {
 *   const { data, isLoading, error, refetch } = useRecentlyPlayed({ limit: 50 });
 *
 *   if (isLoading) return <Skeleton />;
 *   if (error) return <ErrorMessage error={error} />;
 *
 *   return (
 *     <div>
 *       {data.items.map(item => (
 *         <TrackRow key={item.played_at} track={item.track} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useRecentlyPlayed(options?: UseRecentlyPlayedOptions) {
  return useQuery({
    queryKey: recentlyPlayedKeys.list(options),
    queryFn: async (): Promise<RecentlyPlayedResponse> => {
      const params: Record<string, unknown> = {};

      if (options?.limit !== undefined) {
        params['limit'] = options.limit;
      }
      if (options?.before !== undefined) {
        params['before'] = options.before;
      }
      if (options?.after !== undefined) {
        params['after'] = options.after;
      }

      return getApiClient().get<RecentlyPlayedResponse>(
        '/player/recently-played',
        params
      );
    },
    staleTime: 60_000, // 1 minute - history doesn't change frequently
    gcTime: 300_000, // 5 minutes (formerly cacheTime)
    refetchOnWindowFocus: true, // Get fresh data when user returns
    retry: (failureCount, error: any) => {
      // Don't retry on auth errors (401) or forbidden (403)
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
  });
}
